import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportDetailModal = ({ report, onClose }) => {
  const [actionNote, setActionNote] = useState('');
  const [actionTaken, setActionTaken] = useState(null);

  const handleAction = (action) => {
    setActionTaken(action);
    console.log(`Action taken: ${action} for report ${report?.id}`);
    console.log(`Note: ${actionNote}`);
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error bg-error/10';
      case 'medium':
        return 'text-warning bg-warning/10';
      case 'low':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/95 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-card rounded-xl shadow-warm-2xl overflow-hidden animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-muted hover:bg-muted/80 rounded-full transition-smooth"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="overflow-y-auto max-h-[90vh]">
          <div className="p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-error/10 rounded-lg">
                <Icon name="AlertTriangle" size={24} color="var(--color-error)" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-bold mb-2">Report Details</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono text-muted-foreground">{report?.id}</span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(report?.severity)}`}>
                    <Icon name="AlertCircle" size={12} />
                    {report?.severity?.charAt(0)?.toUpperCase() + report?.severity?.slice(1)} Severity
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="User" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Reported User</span>
                </div>
                <p className="text-sm font-medium">{report?.reportedUserName}</p>
                <p className="text-xs text-muted-foreground font-mono">{report?.reportedUserId}</p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Users" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Number of Reports</span>
                </div>
                <p className="text-2xl font-bold">{report?.reporterCount}</p>
                <p className="text-xs text-muted-foreground">unique reporters</p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Report Date</span>
                </div>
                <p className="text-sm font-medium">{formatDate(report?.reportDate)}</p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Clock" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Last Updated</span>
                </div>
                <p className="text-sm font-medium">{formatDate(report?.lastUpdated)}</p>
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="FileText" size={16} color="var(--color-primary)" />
                <span className="text-sm font-caption font-semibold">Reason</span>
              </div>
              <p className="text-sm font-medium mb-2">{report?.reason}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{report?.description}</p>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <label className="text-sm font-caption font-semibold mb-2 block">
                Action Notes (Optional)
              </label>
              <textarea
                value={actionNote}
                onChange={(e) => setActionNote(e?.target?.value)}
                placeholder="Add notes about the action taken..."
                className="w-full h-24 px-3 py-2 bg-background border border-input rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {actionTaken && (
              <div className="p-4 bg-success/10 rounded-lg flex items-center gap-3">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <p className="text-sm font-medium text-success">
                  Action recorded: {actionTaken}
                </p>
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-3 pt-4 border-t border-border">
              <Button
                variant="danger"
                fullWidth
                iconName="Ban"
                iconPosition="left"
                onClick={() => handleAction('Suspend Account')}
                disabled={actionTaken !== null}
              >
                Suspend Account
              </Button>
              <Button
                variant="warning"
                fullWidth
                iconName="AlertTriangle"
                iconPosition="left"
                onClick={() => handleAction('Issue Warning')}
                disabled={actionTaken !== null}
              >
                Issue Warning
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="X"
                iconPosition="left"
                onClick={() => handleAction('Dismiss Report')}
                disabled={actionTaken !== null}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailModal;